// Provenienza del visitatore: il gclid dell'auto-tagging di Google Ads e gli utm_*.
// Arrivano nell'URL della prima pagina e sparirebbero al primo cambio di route; qui li
// leggiamo una volta (captureAttribution, dall'App) e li conserviamo, così il lead o
// l'ordine inviati più tardi li portano con sé (withAttribution, dentro api.ts).
//
// Perché serve: il commerciale decide in CRM se un lead è "qualificato"; per rimandare quel
// verdetto a Google Ads (import conversioni offline, ADR 0026) serve il gclid del clic.
// Un gclid vale 90 giorni lato Google: oltre non ha senso conservarlo.
// Da iOS Google spesso non manda il gclid ma gbraid o wbraid: valgono uguale per l'import
// offline, quindi si conservano tutti e tre.
//
// CONSENSO (ADR 0030): gli identificativi di clic sono un dato a uso pubblicitario e si
// conservano SOLO se il visitatore ha scelto «Accetta tutto» nel banner. Le UTM, anonime,
// si conservano sempre. Se il visitatore atterra con un gclid e accetta dopo (il banner
// compare dopo il primo render), l'identificativo letto dall'URL resta in memoria — non in
// storage — e si salva al momento del consenso. Senza consenso, quelli eventualmente già
// salvati vengono cancellati alla prima lettura. Ogni invio dichiara `consensoAds`, e il
// server tiene gli identificativi solo quando è vero.
//
// SSG-safe: nessun accesso a window fuori dalle funzioni, tutto in try/catch (Safari
// private, quota, storage disabilitato).

import { CONSENT_EVENT, hasAdsConsent } from './analytics'

const KEY = 'sh_attribution'
const TTL_MS = 90 * 24 * 60 * 60 * 1000

export interface Attribution {
  gclid?: string
  gbraid?: string
  wbraid?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  ts: number
}

const PARAMS: Array<[keyof Omit<Attribution, 'ts'>, string]> = [
  ['gclid', 'gclid'],
  ['gbraid', 'gbraid'],
  ['wbraid', 'wbraid'],
  ['utmSource', 'utm_source'],
  ['utmMedium', 'utm_medium'],
  ['utmCampaign', 'utm_campaign'],
]

const CLICK_IDS = ['gclid', 'gbraid', 'wbraid'] as const

type ClickIds = Pick<Attribution, (typeof CLICK_IDS)[number]>

// Identificativi letti dall'URL prima del consenso: solo in memoria, mai in storage.
let inAttesa: ClickIds | null = null
let inAscolto = false

function read(): Attribution | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null
    const a = JSON.parse(raw) as Attribution
    if (!a || typeof a.ts !== 'number' || Date.now() - a.ts > TTL_MS) return null
    if (!hasAdsConsent() && CLICK_IDS.some(k => a[k])) {
      for (const k of CLICK_IDS) delete a[k]
      write(a)
    }
    return a
  } catch {
    return null
  }
}

function write(a: Attribution): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(a))
  } catch {
    // storage non disponibile: la provenienza si perde, il form funziona comunque
  }
}

/** Legge gclid/gbraid/wbraid/utm dall'URL corrente e li conserva. Un nuovo clic pubblicitario sostituisce il precedente. */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return
  try {
    const q = new URLSearchParams(window.location.search)
    const found: Partial<Attribution> = {}
    for (const [campo, param] of PARAMS) {
      const v = q.get(param)?.trim()
      if (v) found[campo] = v.slice(0, 200)
    }
    if (!hasAdsConsent() && CLICK_IDS.some(k => found[k])) {
      inAttesa = {}
      for (const k of CLICK_IDS) {
        if (found[k]) inAttesa[k] = found[k]
        delete found[k]
      }
      ascoltaConsenso()
    }
    if (Object.keys(found).length === 0) return
    // Un utm senza clic non deve cancellare un clic recente: si fondono, il nuovo vince sui
    // campi presenti. Un clic nuovo invece sostituisce tutti gli identificativi del vecchio,
    // altrimenti un gbraid di oggi viaggerebbe insieme al gclid di un altro clic.
    const prev: Partial<Attribution> = { ...(read() ?? {}) }
    if (CLICK_IDS.some(k => found[k])) for (const k of CLICK_IDS) delete prev[k]
    write({ ...prev, ...found, ts: Date.now() })
  } catch {
    // URL non leggibile: niente da conservare
  }
}

// Il consenso arriva dopo l'atterraggio: se è «tutto», gli identificativi in memoria si salvano.
function ascoltaConsenso(): void {
  if (inAscolto) return
  inAscolto = true
  try {
    window.addEventListener(CONSENT_EVENT, () => {
      if (!inAttesa) return
      const pending = inAttesa
      inAttesa = null
      if (!hasAdsConsent()) return
      const prev: Partial<Attribution> = { ...(read() ?? {}) }
      for (const k of CLICK_IDS) delete prev[k]
      write({ ...prev, ...pending, ts: Date.now() })
    })
  } catch {
    // window senza eventi: gli identificativi in memoria si perdono, le UTM restano
  }
}

/** Provenienza conservata, senza il timestamp; vuoto se assente o scaduta. */
export function getAttribution(): Omit<Attribution, 'ts'> {
  const a = read()
  if (!a) return {}
  const { gclid, gbraid, wbraid, utmSource, utmMedium, utmCampaign } = a
  return { gclid, gbraid, wbraid, utmSource, utmMedium, utmCampaign }
}

/**
 * Aggiunge la provenienza a un payload di invio senza sovrascrivere ciò che il form ha
 * impostato esplicitamente (es. una fonte o un coupon di landing).
 */
export function withAttribution<T extends object>(req: T): T {
  const a = getAttribution()
  const out = { ...req } as Record<string, unknown>
  for (const [k, v] of Object.entries(a)) {
    if (v && (out[k] === undefined || out[k] === null || out[k] === '')) out[k] = v
  }
  // Il server tiene gli identificativi solo se il consenso c'è al momento dell'invio.
  const consenso = hasAdsConsent()
  if (!consenso) for (const k of CLICK_IDS) delete out[k]
  if (CLICK_IDS.some(k => out[k])) out.consensoAds = consenso
  return out as T
}
